import React, { useId, useState } from 'react';
import { isEmpty, isNil, uniq } from 'lodash';

import { HANDLER_NOTE_MAX_LENGTH, HANDLER_NOTE_MAX_ROWS } from '~/components/Clive/CliveDialog/constants';
import { handlerNoteCounter } from '~/components/Clive/CliveDialog/utils';
import Button from '~/components/core/Atomic/Buttons/Button';
import TextField from '~/components/core/Molecules/Fields/TextField';

import { CardLabel } from '../../../LabelledText';
import { TEXTS } from '../../../texts';
import type { ConductorQuestion } from '../../../types';

import OptionButton from './OptionButton';
import { useAnswerQuestion } from './useAnswerQuestion';

const OPTIONS = 'Options';
const SOMETHING_ELSE = 'Something else';
/** Something else's detail line. */
const OWN_WORDS = 'Answer in your own words';
const OWN_WORDS_PLACEHOLDER = 'Tell Clive what to do instead';
const ANSWER = 'Answer';

interface AnswerFormProps {
  question: ConductorQuestion;
  onAskUpdate: () => Promise<void>;
}

/**
 * The question while it waits on the handler: its options, and Something else, which opens a box for
 * their own words, then one Answer, which has nothing to send until there is a choice or some words.
 * There is no Dismiss: a handler who disagrees with the question says so in their own words.
 */
const AnswerForm: React.FC<AnswerFormProps> = ({ question, onAskUpdate }) => {
  const answerQuestion = useAnswerQuestion(question.id, onAskUpdate);
  const ownWordsLabelId = useId();
  const [pickedOption, setPickedOption] = useState<string>();
  const [isOwnWords, setIsOwnWords] = useState(false);
  const [ownWords, setOwnWords] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);

  // An option goes as it was offered, which is what the conductor matches it against.
  const answer = isOwnWords ? ownWords.trim() : (pickedOption ?? '');
  const canAnswer = !isEmpty(answer) && !isAnswering;

  /** Picks one of the options, or Something else where there is none. */
  const pickOption = (option?: string): void => {
    setPickedOption(option);
    setIsOwnWords(isNil(option));
  };

  const handleAnswer = async (): Promise<void> => {
    setIsAnswering(true);
    try {
      await answerQuestion(answer, !isOwnWords);
    } catch {
      // Already reported on its way out. The pick and the words stay, to retry with.
    } finally {
      // A read that failed after the answer was taken leaves the card open: pressing again is refused
      // and the read after that settles it, where a held disable would leave nothing to press.
      setIsAnswering(false);
    }
  };

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-8">
        <div role="radiogroup" aria-label={OPTIONS} className="flex flex-col gap-8">
          {uniq(question.options).map((option) => (
            <OptionButton
              key={option}
              label={option}
              isPicked={option === pickedOption}
              onPick={() => pickOption(option)}
            />
          ))}
          <OptionButton label={SOMETHING_ELSE} detail={OWN_WORDS} isPicked={isOwnWords} onPick={() => pickOption()} />
        </div>
        {isOwnWords ? (
          <div className="flex flex-col gap-4">
            {/* Names the box, and stays once the placeholder goes. */}
            <span id={ownWordsLabelId}>
              <CardLabel>{TEXTS.YOUR_ANSWER}</CardLabel>
            </span>
            <TextField
              value={ownWords}
              onChange={setOwnWords}
              variant="outlined"
              multiline
              minRows={3}
              maxRows={HANDLER_NOTE_MAX_ROWS}
              fullWidth
              autoFocus
              // The options' 8px corners, and their 12px padding plus their 1px border, so the words start
              // where the option labels do: MUI draws the outline over the box, where it takes no room.
              // A 1px focus outline in the picked option's blue, in place of MUI's 2px primary one.
              className="[&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:border [&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-blue-600 [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-root]:p-[13px]"
              placeholder={OWN_WORDS_PLACEHOLDER}
              inputProps={{ 'aria-labelledby': ownWordsLabelId, maxLength: HANDLER_NOTE_MAX_LENGTH }}
              helperText={handlerNoteCounter(ownWords)}
            />
          </div>
        ) : null}
      </div>
      <div className="flex">
        <Button variant="contained" color="primary" onClick={handleAnswer} disabled={!canAnswer}>
          {ANSWER}
        </Button>
      </div>
    </div>
  );
};

export default AnswerForm;
